import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, ArrowUpDown } from 'lucide-react';
import type { BookingCycle } from '@shared/schema';

export interface BookingCycleColumnHandlers {
  onEdit: (cycle: BookingCycle) => void;
  onDelete: (cycle: BookingCycle) => void;
  formatDate: (date: Date | string) => string;
}

export function createBookingCycleColumns(
  handlers: BookingCycleColumnHandlers
): ColumnDef<BookingCycle>[] {
  const { onEdit, onDelete, formatDate } = handlers;

  return [
    // Cycle Name column
    {
      accessorKey: 'name',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Cycle Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    },
    // Start Date column
    {
      accessorKey: 'startDate',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Start Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="text-sm">{formatDate(row.original.startDate)}</span>
      ),
    },
    // End Date column
    {
      accessorKey: 'endDate',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            End Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="text-sm">{formatDate(row.original.endDate)}</span>
      ),
    },
    // Status column
    {
      id: 'status',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Status
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: () => (
        <Badge variant="secondary">Active</Badge>
      ),
    },
    // Actions column
    {
      id: 'actions',
      header: () => <div className="text-left">Actions</div>,
      cell: ({ row }) => {
        const cycle = row.original;
        return (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(cycle)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              title="Edit cycle"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(cycle)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Delete cycle"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
      enableSorting: false,
    },
  ];
}








